import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
// Define API_BASE_URL directamente aquí.
import { API_BASE_URL } from '../App';

// --- Material-UI Imports ---
import {
    Container, Box, Button, Paper, TextField, Typography, Select, MenuItem,
    FormControl, InputLabel, Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, CircularProgress, Tooltip, Snackbar, Alert,
    InputAdornment, Chip, Pagination, Autocomplete
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'; // <-- ÍCONO AÑADIDO

// --- TIPOS E INTERFACES ---
interface ConversionRow {
    conversion_id: number;
    conversion_timestamp: string;
    click_timestamp: string; // Añadida la marca de tiempo del click
    payout: string;
    conversion_status: string;
    transaction_id: string;
    internal_click_id: string;
    external_id: string;
    campaign_id: number;
    offer_id: number;
    campaign_name: string;
    offer_name: string;
}

interface PaginationInfo {
    currentPage: number;
    totalPages: number;
    totalRecords: number;
    pageSize: number;
}

interface FilterOption { id: number; name: string; }

// Define el tipo de rango de fecha para mayor claridad
type DateRangeOption = 'today' | 'yesterday' | 'last_7_days' | 'last_30_days' | 'this_month' | 'last_month' | 'custom';

// Helper function to calculate dates based on range selection
const getDateRange = (range: DateRangeOption) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today

    let start = new Date(today);
    let end = new Date(today);

    switch (range) {
        case 'today':
            // start and end are already today
            break;
        case 'yesterday':
            start.setDate(today.getDate() - 1);
            end.setDate(today.getDate() - 1);
            break;
        case 'last_7_days':
            start.setDate(today.getDate() - 6);
            break;
        case 'last_30_days':
            start.setDate(today.getDate() - 29);
            break;
        case 'this_month':
            start = new Date(today.getFullYear(), today.getMonth(), 1);
            end = new Date(today.getFullYear(), today.getMonth() + 1, 0); // Last day of current month
            break;
        case 'last_month':
            start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            end = new Date(today.getFullYear(), today.getMonth(), 0); // Last day of previous month
            break;
        // 'custom' case will be handled by direct TextField input, so no change here
    }
    return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
};

// --- HELPER COMPONENTS ---

/**
 * ConversionsFilterForm Component
 * Renders the filter form for conversions log.
 */
const ConversionsFilterForm: React.FC<{
    startDate: string;
    setStartDate: React.Dispatch<React.SetStateAction<string>>;
    endDate: string;
    setEndDate: React.Dispatch<React.SetStateAction<string>>;
    selectedCampaignId: number | '';
    setSelectedCampaignId: React.Dispatch<React.SetStateAction<number | ''>>;
    selectedOfferId: number | '';
    setSelectedOfferId: React.Dispatch<React.SetStateAction<number | ''>>;
    filterOptions: { campaigns: FilterOption[], offers: FilterOption[] };
    handleFilterSubmit: (event: React.FormEvent) => void;
    isLoading: boolean;
    selectedDateRange: DateRangeOption; // Usar el tipo DateRangeOption aquí
    setSelectedDateRange: React.Dispatch<React.SetStateAction<DateRangeOption>>; // Usar el tipo DateRangeOption aquí
}> = ({
    startDate, setStartDate, endDate, setEndDate,
    selectedCampaignId, setSelectedCampaignId, selectedOfferId, setSelectedOfferId,
    filterOptions, handleFilterSubmit, isLoading,
    selectedDateRange, setSelectedDateRange
}) => {
    return (
        <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: '12px' }}>
            <Typography variant="h6" component="h2" gutterBottom color="text.primary">
                Filtros de Conversiones
            </Typography>
            <Box component="form" onSubmit={handleFilterSubmit} noValidate>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-end">
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Rango de Fecha</InputLabel>
                        <Select
                            value={selectedDateRange}
                            onChange={(e) => {
                                const newRange = e.target.value as DateRangeOption; // Castear al tipo correcto
                                setSelectedDateRange(newRange);
                                if (newRange !== 'custom') {
                                    const { startDate: newStart, endDate: newEnd } = getDateRange(newRange);
                                    setStartDate(newStart);
                                    setEndDate(newEnd);
                                }
                            }}
                            label="Rango de Fecha"
                        >
                            <MenuItem value="today">Hoy</MenuItem>
                            <MenuItem value="yesterday">Ayer</MenuItem>
                            <MenuItem value="last_7_days">Últimos 7 Días</MenuItem>
                            <MenuItem value="last_30_days">Últimos 30 Días</MenuItem>
                            <MenuItem value="this_month">Este Mes</MenuItem>
                            <MenuItem value="last_month">Mes Anterior</MenuItem>
                            <MenuItem value="custom">Personalizado</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        label="Desde"
                        type="date"
                        value={startDate}
                        onChange={e => {
                            setStartDate(e.target.value);
                            setSelectedDateRange('custom'); // Cambiar a personalizado si el usuario edita
                        }}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        disabled={selectedDateRange !== 'custom'} // Deshabilitar si no es personalizado
                    />
                    <TextField
                        fullWidth
                        label="Hasta"
                        type="date"
                        value={endDate}
                        onChange={e => {
                            setEndDate(e.target.value);
                            setSelectedDateRange('custom'); // Cambiar a personalizado si el usuario edita
                        }}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        disabled={selectedDateRange !== 'custom'} // Deshabilitar si no es personalizado
                    />
                    {/* Autocomplete para Campañas */}
                    <Autocomplete
                        fullWidth
                        options={filterOptions.campaigns}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={filterOptions.campaigns.find(c => c.id === selectedCampaignId) || null}
                        onChange={(event, newValue) => {
                            setSelectedCampaignId(newValue ? newValue.id : '');
                        }}
                        renderInput={(params) => <TextField {...params} label="Campaña" variant="outlined" />}
                        filterOptions={(options, { inputValue }) => {
                            const lowercasedInput = inputValue.toLowerCase();
                            return options.filter(option =>
                                option.name.toLowerCase().includes(lowercasedInput) ||
                                String(option.id).includes(lowercasedInput)
                            );
                        }}
                    />
                    {/* Autocomplete para Ofertas */}
                    <Autocomplete
                        fullWidth
                        options={filterOptions.offers}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={filterOptions.offers.find(o => o.id === selectedOfferId) || null}
                        onChange={(event, newValue) => {
                            setSelectedOfferId(newValue ? newValue.id : '');
                        }}
                        renderInput={(params) => <TextField {...params} label="Oferta" variant="outlined" />}
                        filterOptions={(options, { inputValue }) => {
                            const lowercasedInput = inputValue.toLowerCase();
                            return options.filter(option =>
                                option.name.toLowerCase().includes(lowercasedInput) ||
                                String(option.id).includes(lowercasedInput)
                            );
                        }}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        sx={{ height: '56px', minWidth: '150px' }} // Ajustar altura para alinear con TextFields
                        disabled={isLoading}
                        startIcon={<SearchIcon />}
                    >
                        {isLoading ? 'Filtrando...' : 'Aplicar Filtros'}
                    </Button>
                </Stack>
            </Box>
        </Paper>
    );
};

/**
 * ConversionsTableWithPagination Component
 * Renders the table of conversions with search and pagination.
 */
const ConversionsTableWithPagination: React.FC<{
    conversions: ConversionRow[];
    pagination: PaginationInfo | null;
    isLoading: boolean;
    error: string | null;
    searchTerm: string;
    setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
    sortConfig: { key: string; direction: 'ascending' | 'descending' } | null;
    requestSort: (key: keyof ConversionRow) => void;
    handlePageChange: (event: React.ChangeEvent<unknown>, value: number) => void;
    onCopyToClipboard: (text: string) => void; // Nuevo prop para copiar
    averageConversionTime: string | null; // Nuevo prop para el tiempo promedio
}> = ({
    conversions, pagination, isLoading, error,
    searchTerm, setSearchTerm, sortConfig, requestSort, handlePageChange,
    onCopyToClipboard,
    averageConversionTime // Destructurar el nuevo prop
}) => {
    const theme = useTheme();
    const headerCellStyle = { fontWeight: 'bold', backgroundColor: theme.palette.action.hover };

    // Helper para formatear la diferencia de tiempo
    const formatTimeDifference = (start: string | undefined, end: string): string => {
        if (!start || !end) return 'N/A';
        const startDate = new Date(start);
        const endDate = new Date(end);

        const diffMs = endDate.getTime() - startDate.getTime();

        if (isNaN(diffMs) || diffMs < 0) return 'Inválido';

        const diffSeconds = Math.floor(diffMs / 1000);
        const diffMinutes = Math.floor(diffSeconds / 60);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);

        const remainingHours = diffHours % 24;
        const remainingMinutes = diffMinutes % 60;
        const remainingSeconds = diffSeconds % 60;

        let result = '';
        if (diffDays > 0) result += `${diffDays}d `;
        if (remainingHours > 0) result += `${remainingHours}h `;
        if (remainingMinutes > 0) result += `${remainingMinutes}m `;
        // Solo muestra segundos si la diferencia total es menor a un minuto y no hay otras unidades mayores
        if (diffDays === 0 && remainingHours === 0 && remainingMinutes === 0 && remainingSeconds >= 0) {
            result += `${remainingSeconds}s`;
        } else if (result === '' && diffSeconds === 0) {
            result = '0s'; // Para diferencias muy pequeñas, mostrar 0s
        }

        return result.trim();
    };


    const getSortIcon = (key: keyof ConversionRow) => {
        if (!sortConfig || sortConfig.key !== key) {
            return <SortIcon fontSize="small" sx={{ ml: 0.5 }} />;
        }
        return sortConfig.direction === 'ascending' ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />;
    };

    const processedData = useMemo(() => {
        let processableData = [...conversions];

        if (searchTerm) {
            processableData = processableData.filter(row =>
                Object.values(row).some(value =>
                    String(value).toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
        }

        if (sortConfig !== null) {
            processableData.sort((a, b) => {
                const aValue = a[sortConfig.key as keyof ConversionRow];
                const bValue = b[sortConfig.key as keyof ConversionRow];

                // Manejo especial para fechas para asegurar un ordenamiento correcto
                if (sortConfig.key === 'conversion_timestamp' || sortConfig.key === 'click_timestamp') {
                    const dateA = new Date(aValue as string).getTime();
                    const dateB = new Date(bValue as string).getTime();
                    if (dateA < dateB) return sortConfig.direction === 'ascending' ? -1 : 1;
                    if (dateA > dateB) return sortConfig.direction === 'ascending' ? 1 : -1;
                    return 0;
                }
                
                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        
        return processableData;
    }, [conversions, searchTerm, sortConfig]);


    return (
        <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: '12px', mt: 4 }}>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h5" component="h2" gutterBottom color="text.primary">
                    Resultados del Log de Conversiones
                </Typography>
                {averageConversionTime && (
                    <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
                        Tiempo promedio de conversión: <Chip label={averageConversionTime} color="secondary" size="small" />
                    </Typography>
                )}
                <TextField
                    fullWidth
                    label="Buscar en los resultados de esta página"
                    variant="outlined"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 2 }}
                />

                {isLoading && conversions.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Cargando conversiones...</Typography>
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ m: 2 }}>Error: {error}</Alert>
                ) : processedData.length === 0 ? (
                    <Typography sx={{ textAlign: 'center', p: 4 }} color="text.secondary">No se encontraron conversiones para esta selección.</Typography>
                ) : (
                    <TableContainer>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {/* Columnas reordenadas */}
                                    <TableCell sx={headerCellStyle} onClick={() => requestSort('internal_click_id')} style={{ cursor: 'pointer' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            ID Interno {getSortIcon('internal_click_id')}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={headerCellStyle} onClick={() => requestSort('external_id')} style={{ cursor: 'pointer' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            ID Externo {getSortIcon('external_id')}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={headerCellStyle} onClick={() => requestSort('campaign_name')} style={{ cursor: 'pointer' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            Campaña {getSortIcon('campaign_name')}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={headerCellStyle} onClick={() => requestSort('offer_name')} style={{ cursor: 'pointer' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            Oferta {getSortIcon('offer_name')}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={headerCellStyle} onClick={() => requestSort('click_timestamp')} style={{ cursor: 'pointer' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            Fecha Click {getSortIcon('click_timestamp')}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={headerCellStyle} onClick={() => requestSort('conversion_timestamp')} style={{ cursor: 'pointer' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            Fecha Conversión {getSortIcon('conversion_timestamp')}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={headerCellStyle}>
                                        Tiempo Conv.
                                    </TableCell>
                                    <TableCell sx={headerCellStyle} onClick={() => requestSort('payout')} style={{ cursor: 'pointer' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            Payout {getSortIcon('payout')}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={headerCellStyle} onClick={() => requestSort('conversion_status')} style={{ cursor: 'pointer' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            Estado {getSortIcon('conversion_status')}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {processedData.map((conv) => (
                                    // Eliminado el onClick de la fila completa para el modal
                                    <TableRow key={conv.conversion_id} hover> 
                                        <TableCell>
                                            <Tooltip title="Copiar ID Interno">
                                                <Button size="small" onClick={() => onCopyToClipboard(conv.internal_click_id)} endIcon={<ContentCopyIcon sx={{ fontSize: '0.8em' }} />}>
                                                    {conv.internal_click_id}
                                                </Button>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Copiar ID Externo">
                                                <Button size="small" onClick={() => onCopyToClipboard(conv.external_id)} endIcon={<ContentCopyIcon sx={{ fontSize: '0.8em' }} />}>
                                                    {conv.external_id}
                                                </Button>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>{conv.campaign_name}</TableCell>
                                        <TableCell>{conv.offer_name}</TableCell>
                                        <TableCell>{conv.click_timestamp ? new Date(conv.click_timestamp).toLocaleString('es-MX') : 'N/A'}</TableCell>
                                        <TableCell>{new Date(conv.conversion_timestamp).toLocaleString('es-MX')}</TableCell>
                                        <TableCell>
                                            {formatTimeDifference(conv.click_timestamp, conv.conversion_timestamp)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={`$${parseFloat(conv.payout).toFixed(2)}`}
                                                color="primary"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={conv.conversion_status}
                                                color={conv.conversion_status === 'approved' ? 'success' : conv.conversion_status === 'pending' ? 'info' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                
                {pagination && processedData.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Pagination
                            count={pagination.totalPages}
                            page={pagination.currentPage}
                            onChange={handlePageChange}
                            color="primary"
                            showFirstButton
                            showLastButton
                            disabled={isLoading}
                            sx={{ mb: { xs: 2, sm: 0 }, mr: { sm: 2 } }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            Página {pagination.currentPage} de {pagination.totalPages} (Total: {pagination.totalRecords} conversiones)
                        </Typography>
                    </Box>
                )}
            </Box>
        </Paper>
    );
};


const ConversionsLogPage: React.FC = () => {
    // --- ESTADOS DEL COMPONENTE ---
    const [conversions, setConversions] = useState<ConversionRow[]>([]);
    const [pagination, setPagination] = useState<PaginationInfo | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    const [averageConversionTime, setAverageConversionTime] = useState<string | null>(null); // Nuevo estado para el promedio

    // Estado para los filtros del formulario
    const [selectedDateRange, setSelectedDateRange] = useState<DateRangeOption>('today');
    const initialDates = getDateRange(selectedDateRange); // Get initial dates based on default range
    const [startDate, setStartDate] = useState<string>(initialDates.startDate);
    const [endDate, setEndDate] = useState<string>(initialDates.endDate);
    const [selectedCampaignId, setSelectedCampaignId] = useState<number | ''>('');
    const [selectedOfferId, setSelectedOfferId] = useState<number | ''>('');
    
    // Estado para la tabla interactiva (manejar búsqueda y ordenamiento)
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'ascending' | 'descending' } | null>({key: 'conversion_timestamp', direction: 'descending'});
    
    // Opciones para los selectores de filtro
    const [filterOptions, setFilterOptions] = useState<{ campaigns: FilterOption[], offers: FilterOption[] }>({ campaigns: [], offers: [] });

    // --- LÓGICA DE DATOS ---

    // Función para copiar al portapapeles y mostrar notificación
    const handleCopyToClipboard = useCallback(async (text: string) => {
        try {
            // Usar document.execCommand para compatibilidad en entornos de iframe
            const tempInput = document.createElement('textarea');
            tempInput.value = text;
            document.body.appendChild(tempInput);
            tempInput.select();
            document.execCommand('copy');
            document.body.removeChild(tempInput);
            setNotification({ type: 'success', message: `"${text}" copiado al portapapeles!` });
        } catch (err) {
            setNotification({ type: 'error', message: 'Error al copiar. Por favor, copia manualmente.' });
            console.error('Error al copiar:', err);
        }
    }, []);

    // Helper para formatear un número de milisegundos a un formato legible (días, horas, minutos, segundos)
    const formatMillisecondsToTime = (ms: number): string => {
        if (isNaN(ms) || ms < 0) return 'N/A';

        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        const remainingHours = hours % 24;
        const remainingMinutes = minutes % 60;
        const remainingSeconds = seconds % 60;

        let result = '';
        if (days > 0) result += `${days}d `;
        if (remainingHours > 0) result += `${remainingHours}h `;
        if (remainingMinutes > 0) result += `${remainingMinutes}m `;
        // Siempre mostrar segundos si no hay unidades mayores o si es muy corto
        if (result === '' || remainingSeconds > 0) {
            result += `${remainingSeconds}s`;
        }
        
        return result.trim();
    };


    // Calcula el tiempo promedio de conversión
    useEffect(() => {
        if (conversions.length > 0) {
            let totalTimeDifferenceMs = 0;
            let validConversionsCount = 0;

            conversions.forEach(conv => {
                if (conv.click_timestamp && conv.conversion_timestamp) {
                    const clickDate = new Date(conv.click_timestamp);
                    const conversionDate = new Date(conv.conversion_timestamp);
                    const diffMs = conversionDate.getTime() - clickDate.getTime();
                    if (!isNaN(diffMs) && diffMs >= 0) {
                        totalTimeDifferenceMs += diffMs;
                        validConversionsCount++;
                    }
                }
            });

            if (validConversionsCount > 0) {
                const averageMs = totalTimeDifferenceMs / validConversionsCount;
                setAverageConversionTime(formatMillisecondsToTime(averageMs));
            } else {
                setAverageConversionTime('N/A');
            }
        } else {
            setAverageConversionTime(null); // Reset when no conversions
        }
    }, [conversions]);


    // Carga las opciones para los filtros (campañas, ofertas) una sola vez
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [campaignsRes, offersRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/campaigns`),
                    axios.get(`${API_BASE_URL}/offers`),
                ]);
                setFilterOptions({
                    campaigns: campaignsRes.data.map((c: any) => ({ id: c.id, name: c.name })),
                    offers: offersRes.data.map((o: any) => ({ id: o.id, name: o.name })),
                });
            } catch (e: any) {
                console.error("Error cargando opciones de filtros para el log de conversiones", e);
                setNotification({ type: 'error', message: e.response?.data?.message || "Error al cargar opciones de filtro." });
            }
        };
        fetchOptions();
    }, []);

    // Hook para recalcular startDate y endDate cuando cambia selectedDateRange
    useEffect(() => {
        if (selectedDateRange !== 'custom') {
            const { startDate: newStart, endDate: newEnd } = getDateRange(selectedDateRange);
            setStartDate(newStart);
            setEndDate(newEnd);
        }
    }, [selectedDateRange]);


    // Hook principal que llama a la API cada vez que cambian los filtros o la página
    const fetchConversions = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        setAverageConversionTime(null); // Reset average time when fetching new data

        try {
            const apiEndDate = new Date(endDate);
            apiEndDate.setDate(apiEndDate.getDate() + 1); // Incluir el día final completo

            let url = `${API_BASE_URL}/conversions?page=${currentPage}&limit=25&startDate=${startDate}&endDate=${apiEndDate.toISOString().split('T')[0]}`;
            
            if (selectedCampaignId) url += `&campaign_id=${selectedCampaignId}`;
            if (selectedOfferId) url += `&offer_id=${selectedOfferId}`;

            const response = await axios.get(url);
            setConversions(response.data.data); 
            setPagination(response.data.pagination);
        } catch (err: any) {
            const errorMessage = err.response ? err.response.data.message : "Error de red o CORS.";
            setError(errorMessage);
            setNotification({ type: 'error', message: errorMessage });
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate, selectedCampaignId, selectedOfferId, currentPage]);

    useEffect(() => {
        fetchConversions();
    }, [fetchConversions]);

    // --- MANEJADORES DE EVENTOS ---
    const handleFilterSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setCurrentPage(1); // Al aplicar filtros, siempre volvemos a la página 1
        // fetchConversions se ejecutará debido al cambio en currentPage
    };
    
    const requestSort = (key: keyof ConversionRow) => {
        let direction: 'ascending' | 'descending' = 'ascending';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value);
    };

    // --- RENDERIZADO (VISTA) ---
    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Snackbar para notificaciones */}
            <Snackbar
                open={!!notification}
                autoHideDuration={6000}
                onClose={() => setNotification(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setNotification(null)} severity={notification?.type} sx={{ width: '100%' }}>
                    {notification?.message}
                </Alert>
            </Snackbar>

            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                <PlaylistAddCheckIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                Log de Conversiones
            </Typography>
            
            {/* Formulario de filtros */}
            <ConversionsFilterForm
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                selectedCampaignId={selectedCampaignId}
                setSelectedCampaignId={setSelectedCampaignId}
                selectedOfferId={selectedOfferId}
                setSelectedOfferId={setSelectedOfferId}
                filterOptions={filterOptions}
                handleFilterSubmit={handleFilterSubmit}
                isLoading={isLoading}
                selectedDateRange={selectedDateRange}
                setSelectedDateRange={setSelectedDateRange}
            />

            {/* Tabla de conversiones con búsqueda y paginación */}
            <ConversionsTableWithPagination
                conversions={conversions}
                pagination={pagination}
                isLoading={isLoading}
                error={error}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                sortConfig={sortConfig}
                requestSort={requestSort}
                handlePageChange={handlePageChange}
                onCopyToClipboard={handleCopyToClipboard} // Pasar la función de copiar
                averageConversionTime={averageConversionTime} // Pasar el tiempo promedio
            />
        </Container>
    );
};

export default ConversionsLogPage;